import { UploadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Spin, Upload } from "antd";
import { UploadProps } from "antd/es/upload";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import { OPERATION_KEYS, operationServices } from "~/services/operation";

export const ModalImportStaff: FC = () => {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const notification = useNotification();

  const handleUpload: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    // Validate file
    if (!(file instanceof File) || file.type !== "text/csv") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a valid CSV file.",
      });
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "The CSV file must be smaller than 10MB.",
      });
      onError?.(new Error("File too large"));
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await operationServices.importStaffs(formData);
      notification.success({
        message: "Staffs imported successfully!",
        description: "The staffs have been added from the CSV file.",
      });
      queryClient.invalidateQueries({
        queryKey: OPERATION_KEYS.GET_ALL_STAFF({}),
      });
      setOpen(false);
      onSuccess?.({});
    } catch (err: any) {
      notification.error({
        message: "Error importing staffs",
        description:
          err.message || "There was an error processing the CSV file.",
      });
      onError?.(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disabled={isUploading}
        className="mr-4"
        icon={<UploadOutlined />}
      >
        <span>Import CSV</span>
      </Button>
      <Modal
        open={open}
        title="Import staffs from CSV"
        onCancel={() => setOpen(false)}
        footer={null}
      >
        <Spin spinning={isUploading}>
          <Upload
            accept=".csv"
            customRequest={handleUpload}
            showUploadList={false}
            disabled={isUploading}
          >
            <Button icon={<UploadOutlined />} disabled={isUploading}>
              Click to Upload CSV
            </Button>
          </Upload>
          <div className="mt-4 text-sm text-gray-500">
            <p>Please upload a CSV file with the correct format.</p>
            <p>Maximum file size: 10MB.</p>
          </div>
        </Spin>
      </Modal>
    </>
  );
};
